import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtDate } from "@/lib/format";
import { Send } from "lucide-react";

export default function Messages() {
  const [messages, setMessages] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = () => api.get("/portal/messages").then(({ data }) => setMessages(data.messages)).catch(() => setMessages([]));
  useEffect(() => { load(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    const body = text.trim();
    setText("");
    try {
      const { data } = await api.post("/portal/messages", { text: body });
      setMessages((m) => [...(m || []), data]);
    } catch (e) { setText(body); } finally { setSending(false); }
  };

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <div className="space-y-6" data-testid="messages-page">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight">Secure messages</h1>
        <p className="text-sm text-muted-foreground mt-1">Private channel with MentorAI Client Services.</p>
      </div>

      <Card className="rounded-xl ring-1 ring-border flex flex-col h-[calc(100vh-16rem)] min-h-[420px]">
        <div data-testid="messages-thread" className="flex-1 overflow-y-auto p-5 space-y-4">
          {!messages ? (
            <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className={`h-14 rounded-2xl ${i % 2 ? "w-2/3" : "w-1/2 ml-auto"}`} />)}</div>
          ) : messages.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground">No messages yet. Start the conversation below.</div>
          ) : messages.map((m) => (
            <div key={m.message_id} className={`flex ${m.sender === "client" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 ${m.sender === "client" ? "bg-muted text-foreground" : "bg-brand/10 text-foreground ring-1 ring-brand/15"}`}>
                <div className="text-[11px] text-muted-foreground mb-0.5">{m.sender === "client" ? "You" : m.sender_name} · {fmtDate(m.timestamp, true)}</div>
                <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <form data-testid="messages-composer" onSubmit={send} className="border-t p-3 flex items-end gap-2">
          <Textarea data-testid="messages-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKey} rows={1} placeholder="Write a message… (Enter to send)" className="resize-none min-h-[44px] max-h-32" />
          <Button data-testid="messages-send-button" type="submit" disabled={sending || !text.trim()} className="rounded-xl bg-[#0EA5A4] hover:bg-[#0B8E8D] text-white h-11 px-4"><Send className="size-4" /></Button>
        </form>
      </Card>
    </div>
  );
}
